import { Modal } from "./Modal.tsx";
import clsx from "clsx";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { helpers } from "../util/helpers.ts";
import { logEvent } from "../util/logEvent.ts";
import { toWorker } from "../util/toWorker.ts";
import { useLocalPartial } from "../util/local.ts";
import { RatingsStatsPopover } from "../components/RatingsStatsPopover/index.tsx";
import { MobileStatusMetric } from "./MobileStatusMetric.tsx";
import { Mood } from "../components/Mood.tsx";
import { PlayerPicture } from "../components/PlayerPicture.tsx";
import { useRef, useState } from "react";
import i18n from "../util/i18n.ts";
import { formatPlayerNameFromLegacyName } from "../util/playerNames.ts";
import { localizeNegotiationError } from "../util/localizeNegotiationError.ts";
import { createMobileReturnUrl } from "../util/mobilePageContext.ts";
import { getSignSuccessToastText } from "../util/successToast.ts";
import { getSigningHighlightCard, isBigSigning } from "../util/feedbackHeadline.ts";
import { emitHighlightCard } from "../util/highlightCard.ts";
import { pronounLocalized } from "../util/pronounLocalized.ts";
import type api from "../../../../server/worker/api/index.ts";
import {
  type FinanceCapState,
  getFinanceCapStatus,
} from "../views/TeamFinances/financeStatusMetrics.ts";
import { MarketRatingStatusMetric } from "../views/MarketRatingStatusMetric.tsx";

type NegotaitionModalProps = Exclude<
  Awaited<ReturnType<typeof api.main.getNegotiationProps>>,
  string
>;

const getMobileScrollY = () => (typeof window !== "undefined" ? window.scrollY : undefined);

const createNegotiationModalPlayerHref = (pid: number, resigning: boolean) =>
  createMobileReturnUrl(helpers.leagueUrl(["player", pid]), {
    activeTab: resigning ? "resigning" : "freeAgents",
    filterKey: "negotiationModal",
    filterValue: resigning ? "resigning" : "freeAgent",
    originSection: "market",
    pageID: "negotiationList",
    scrollY: getMobileScrollY(),
  });

const SignButton = ({
  pid,
  ovr,
  amount,
  exp,
  years,
  playerName,
  resigning,
  disabledReason,
  onSuccess,
}: {
  pid: number;
  ovr: number;
  amount: number;
  exp: number;
  years: number;
  playerName: string;
  resigning: boolean;
  disabledReason: string | undefined;
  onSuccess: () => void;
}) => {
  const localizedDisabledReason =
    disabledReason !== undefined ? localizeNegotiationError(disabledReason) : undefined;

  const button = (
    <button
      className={`btn ${disabledReason !== undefined ? "btn-secondary" : "btn-success"}`}
      data-negotiation-sign-offer-amount={amount}
      data-negotiation-sign-offer-exp={exp}
      data-negotiation-sign-offer-pid={pid}
      data-negotiation-sign-offer-years={years}
      disabled={disabledReason !== undefined}
      onClick={async () => {
        const errorMessage = await toWorker("main", "acceptContractNegotiation", {
          pid,
          amount: Math.round(amount * 1000),
          exp,
        });
        if (errorMessage !== undefined) {
          logEvent({
            type: "error",
            text: localizeNegotiationError(errorMessage),
            saveToDb: false,
          });
        }

        // N4-2/N4-1 success feedback: a signed contract used to be fully
        // silent (only failures surfaced a red toast). A regular signing fires
        // the L1 success toast; a headline (star-level) signing is promoted to
        // an L2 highlight card *instead* (never both). The toast builder returns
        // undefined on failure, so the red toast above stays the only feedback
        // there.
        if (errorMessage === undefined) {
          const amountText = helpers.formatCurrency(amount, "M");
          if (isBigSigning(ovr)) {
            emitHighlightCard(
              getSigningHighlightCard({
                resigning,
                playerName,
                years,
                amountText,
                url: helpers.leagueUrl(["player", pid]),
              }),
            );
          } else {
            const successText = getSignSuccessToastText({
              errorMessage,
              resigning,
              playerName,
              years,
              amountText,
            });
            if (successText !== undefined) {
              logEvent({ type: "success", text: successText, saveToDb: false });
            }
          }
          onSuccess();
        }
      }}
    >
      {i18n.t("negotiation.sign", { ns: "ui", defaultValue: "Sign" })}
      <span className="d-none d-sm-inline">
        {" "}
        {i18n.t("negotiation.contract", { ns: "ui", defaultValue: "Contract" })}
      </span>
    </button>
  );

  if (disabledReason === undefined) {
    return button;
  }

  // B4: on mobile the reason a contract cannot be signed is important
  // information, so show it as visible text instead of behind a tooltip.
  // This also sidesteps the position-fixed tooltip hack below, which breaks
  // once the (scrollable) mobile modal scrolls.
  if (window.mobile) {
    return (
      <div className="text-end" style={{ maxWidth: 170 }}>
        {button}
        <div className="small text-body-secondary mt-1">{localizedDisabledReason}</div>
      </div>
    );
  }

  // CSS/HTML hacks! (desktop only)
  // position-fixed is for https://stackoverflow.com/a/75264190/786644 https://github.com/react-bootstrap/react-bootstrap/issues/6563 otherwise the scrollback flicker appears/disappears on desktop when the react-bootstrap Tooltip is shown. However this breaks if you scroll, but this page doesn't need to scroll on desktop.
  // Wrapper div around button is because otherwise there is no hover over the disabled button and no tooltip is shown.
  return (
    <OverlayTrigger
      overlay={<Tooltip className="position-fixed">{localizedDisabledReason}</Tooltip>}
      rootClose
      trigger={["hover", "focus", "click"]}
    >
      <div>{button}</div>
    </OverlayTrigger>
  );
};

const widthStyle = { maxWidth: 575 };

const negotiationCapStatusLabel = (state: FinanceCapState | undefined, capSpace: number) => {
  const amount = helpers.formatCurrency(Math.abs(capSpace), "M");
  if (state === "over") {
    return i18n.t("leagueDashboard.capOverStatus", {
      ns: "ui",
      defaultValue: "{{amount}} over the limit",
      amount,
    });
  }
  if (state === "near") {
    return i18n.t("leagueDashboard.capNearStatus", {
      ns: "ui",
      defaultValue: "Near the limit",
    });
  }
  if (state === "available") {
    return i18n.t("leagueDashboard.capAvailableStatus", {
      ns: "ui",
      defaultValue: "{{amount}} available",
      amount,
    });
  }
  return i18n.t("teamFinances.valueUnavailable", {
    ns: "ui",
    defaultValue: "Not available",
  });
};

const NegotiationHeader = ({
  capSpace,
  challengeNoRatings,
  payroll,
  p,
  resigning,
  salaryCap,
  salaryCapType,
  t,
}: Pick<
  NegotaitionModalProps,
  | "capSpace"
  | "challengeNoRatings"
  | "payroll"
  | "p"
  | "resigning"
  | "salaryCap"
  | "salaryCapType"
  | "t"
>) => {
  const { gender } = useLocalPartial(["gender"]);
  const playerName = formatPlayerNameFromLegacyName(p.name);
  const playerHref = createNegotiationModalPlayerHref(p.pid, resigning);
  const capStatus = getFinanceCapStatus({ capSpace, payrollLimit: salaryCap });

  let message;
  if (salaryCapType === "soft" && resigning) {
    const He = pronounLocalized(gender, "He");
    const him = pronounLocalized(gender, "him");
    const he = pronounLocalized(gender, "he");
    message = (
      <>
        {i18n.t("negotiation.softCapResigning1", {
          ns: "ui",
          defaultValue: "You are allowed to go over the salary cap when re-signing players.",
        })}{" "}
        <b>
          {i18n.t("negotiation.softCapResigning2", {
            ns: "ui",
            defaultValue: "If you do not come to an agreement here,",
          })}{" "}
          <a href={playerHref}>{playerName}</a>{" "}
          {i18n.t("negotiation.softCapResigning3", {
            ns: "ui",
            defaultValue: "will become a free agent.",
          })}
        </b>{" "}
        {i18n.t("negotiation.softCapResigning4", {
          ns: "ui",
          defaultValue: `${He} will then be able to sign with any team, and you won't be able to go over the salary cap to sign ${him} unless ${he}'s asking for a minimum contract.`,
          He,
          him,
          he,
        })}
      </>
    );
  }

  return (
    <>
      <div className="d-flex gap-2 mb-3" style={widthStyle}>
        <div
          style={{
            height: 90,
            width: 60,
            marginTop: p.imgURL ? 0 : -10,
          }}
          className="flex-shrink-0 d-flex justify-content-center align-items-center"
        >
          <PlayerPicture face={p.face} imgURL={p.imgURL} colors={t.colors} jersey={t.jersey} lazy />
        </div>
        <div className="d-flex flex-column justify-content-end">
          <div className="d-flex flex-wrap gap-2">
            <h1 className="mb-0 text-nowrap">
              <a href={playerHref}>{playerName}</a>
            </h1>
            <div className="d-flex align-items-center">
              <Mood defaultType="user" host="negotiation-modal" p={p} playerHref={playerHref} />
              <RatingsStatsPopover pid={p.pid} defaultWatch={p.watch} playerHref={playerHref} />
            </div>
          </div>
          <div>
            {i18n.t("negotiation.age", {
              ns: "ui",
              defaultValue: "{{age}} years old",
              age: p.age,
            })}
            {!challengeNoRatings ? (
              <span className="d-none d-sm-inline">
                ;{" "}
                {i18n.t("negotiation.overall", {
                  ns: "ui",
                  defaultValue: "Overall",
                })}
                : {p.ratings.ovr};{" "}
                {i18n.t("negotiation.potential", {
                  ns: "ui",
                  defaultValue: "Potential",
                })}
                : {p.ratings.pot}
              </span>
            ) : null}
          </div>
          {!challengeNoRatings ? (
            <div className="d-sm-none mt-2">
              <MarketRatingStatusMetric
                label={i18n.t("marketMetrics.playerLevel", {
                  ns: "ui",
                  defaultValue: "Player level",
                })}
                potential={p.ratings.pot}
                rating={p.ratings.ovr}
              />
            </div>
          ) : null}
          <div>
            {resigning
              ? i18n.t("negotiation.reSigning", { ns: "ui", defaultValue: "Re-signing" })
              : i18n.t("negotiation.freeAgent", { ns: "ui", defaultValue: "Free Agent" })}
          </div>
        </div>
        <div className="ms-auto d-none d-sm-flex flex-column justify-content-end align-items-end text-nowrap">
          <div>
            {i18n.t("negotiation.payroll", { ns: "ui", defaultValue: "Payroll:" })}{" "}
            {helpers.formatCurrency(payroll, "M")}
          </div>
          {salaryCapType !== "none" ? (
            <>
              <div>
                {i18n.t("negotiation.salaryCap", { ns: "ui", defaultValue: "Salary Cap:" })}{" "}
                {helpers.formatCurrency(salaryCap, "M")}
              </div>
              <div>
                {i18n.t("negotiation.capSpace", { ns: "ui", defaultValue: "Cap Space:" })}{" "}
                {helpers.formatCurrency(capSpace, "M")}
              </div>
            </>
          ) : null}
        </div>
      </div>
      {salaryCapType !== "none" ? (
        <div className="d-sm-none mb-3">
          <MobileStatusMetric
            detail={helpers.formatCurrency(capSpace, "M")}
            label={i18n
              .t("negotiation.capSpace", {
                ns: "ui",
                defaultValue: "Cap Space:",
              })
              .replace(/:\s*$/, "")}
            score={capStatus.score}
            value={negotiationCapStatusLabel(capStatus.state, capSpace)}
          />
        </div>
      ) : null}
      {message ? (
        <div className="alert alert-warning py-2 mb-3" data-negotiation-resigning-risk="">
          {message}
        </div>
      ) : null}
    </>
  );
};

const Negotiation = ({
  contractOptions,
  close,
  p,
  resigning,
}: Pick<NegotaitionModalProps, "contractOptions" | "p" | "resigning"> & {
  close: () => void;
}) => {
  const playerName = formatPlayerNameFromLegacyName(p.name);
  return (
    <>
      <div className="list-group" style={widthStyle}>
        {contractOptions.map((contract, i) => {
          return (
            <div
              key={i}
              className={clsx("d-flex align-items-center list-group-item", {
                "list-group-item-success": contract.smallestAmount,
              })}
              data-negotiation-offer-amount={contract.amount}
              data-negotiation-offer-enabled={
                contract.disabledReason === undefined ? "true" : "false"
              }
              data-negotiation-offer-exp={contract.exp}
              data-negotiation-offer-pid={p.pid}
              data-negotiation-offer-smallest={contract.smallestAmount ? "true" : "false"}
              data-negotiation-offer-years={contract.years}
            >
              <div className="flex-grow-1">
                <b>{helpers.formatCurrency(contract.amount, "M")}</b>
                {i18n.t("negotiation.perYear", {
                  ns: "ui",
                  defaultValue: "/year",
                })}{" "}
                {i18n.t("negotiation.forYears", {
                  ns: "ui",
                  defaultValue: "for",
                })}{" "}
                {contract.years}{" "}
                {i18n.t("negotiation.year", {
                  ns: "ui",
                  defaultValue: "year",
                  count: contract.years,
                })}{" "}
                (
                {i18n.t("negotiation.through", {
                  ns: "ui",
                  defaultValue: "through",
                })}{" "}
                {contract.exp})
              </div>

              <SignButton
                pid={p.pid}
                ovr={p.ratings.ovr}
                amount={contract.amount}
                exp={contract.exp}
                years={contract.years}
                playerName={playerName}
                resigning={resigning}
                disabledReason={contract.disabledReason}
                onSuccess={close}
              />
            </div>
          );
        })}
      </div>
    </>
  );
};

export const NegotiationModal = ({
  close,
  negotiationProps,
  show,
}: {
  close: () => void;
  negotiationProps: NegotaitionModalProps | undefined;
  show: boolean;
}) => {
  return (
    <Modal show={show} onHide={close}>
      {/* B4: in-flow header close button instead of a position:absolute
			    btn-close floating over the body (which overlapped the player
			    name row on narrow viewports). */}
      <Modal.Header
        closeButton
        closeLabel={i18n.t("common.close", {
          ns: "ui",
          defaultValue: "Close",
        })}
        className="border-0 pb-0"
      />
      <Modal.Body className="pt-0" data-negotiation-modal-pid={negotiationProps?.p.pid}>
        {negotiationProps ? (
          <>
            <NegotiationHeader {...negotiationProps} />
            <Negotiation {...negotiationProps} close={close} />
          </>
        ) : null}
      </Modal.Body>
    </Modal>
  );
};

export const useNegotiaionModal = () => {
  const [state, setState] = useState<
    | {
        status: "loaded";
        props: NegotaitionModalProps;
        show: boolean;
      }
    | {
        status: "init";
      }
  >({
    status: "init",
  });

  const show = state.status === "loaded" && state.show;

  const loadingPid = useRef<number | undefined>(undefined);

  const props = {
    close: () => {
      if (show) {
        setState((state) => {
          if (state.status === "loaded") {
            return { ...state, show: false };
          }

          return state;
        });
      }
    },
    negotiationProps: state.status === "loaded" ? state.props : undefined,
    show,
  };

  return {
    negotiate: async (pid: number) => {
      loadingPid.current = pid;

      const newProps = await toWorker("main", "getNegotiationProps", pid);

      if (loadingPid.current !== pid) {
        // Must have clicked another button
        return;
      }

      if (typeof newProps === "string") {
        logEvent({
          type: "error",
          text: newProps,
          saveToDb: false,
        });
      } else {
        setState({
          status: "loaded",
          props: newProps,
          show: true,
        });
      }
    },
    props,
  };
};
